import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from 'axios'
import KeycloakService from '../utils/KeycloakService'
import { API_BASE_URL } from '../utils/ApiEndpoints'

// Service Response interface for consistent return type
export interface ServiceResponse<T = any> {
  data: T | null
  success: boolean
  message: string
  status?: number
}

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  // Remove default Content-Type header - will be set in interceptor
})

// Request interceptor to add auth token and handle Content-Type
apiClient.interceptors.request.use(
  (config) => {
    try {
      // Get token from KeycloakService with error handling
      const token = KeycloakService?.getToken?.()

      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }

      // Handle Content-Type based on data type
      if (config?.data instanceof FormData) {
        // Remove Content-Type header to let browser set it automatically with boundary
        delete config.headers?.['Content-Type']
      } else if (!config.headers?.['Content-Type']) {
        // Set JSON content type for non-FormData requests
        config.headers = config.headers || {}
        config.headers['Content-Type'] = 'application/json'
      }

      // Log request for debugging (remove in production)
      // loggger.info('API Request:', {
      //   method: config?.method?.toUpperCase(),
      //   url: config?.url,
      //   baseURL: config?.baseURL,
      //   headers: config?.headers,
      //   isFormData: config?.data instanceof FormData,
      // })

      return config
    } catch (error) {
      console.error('Request interceptor setup error:', error)
      return config // Return config even if there's an error to prevent request failure
    }
  },
  (error: AxiosError) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for handling common response scenarios
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      // Log successful response (remove in production)
      // loggger.info('API Response:', {
      //   status: response?.status,
      //   url: response?.config?.url,
      //   data: response?.data,
      //   success: response?.data?.success,
      // })

      return response
    } catch (error) {
      console.error('Response interceptor logging error:', error)
      return response // Return response even if logging fails
    }
  },
  async (error: AxiosError) => {
    try {
      // Handle common error scenarios with optional chaining
      if (error?.response) {
        const { status, data } = error.response

        switch (status) {
          case 401:
            // Unauthorized - token might be expired
            console.error('Unauthorized access - redirecting to login')
            await KeycloakService?.doLogout?.()?.catch((logoutError) => {
              console.error('Logout error:', logoutError)
            })
            break

          case 403:
            console.error('Forbidden - insufficient permissions')
            break

          case 404:
            console.error('Resource not found')
            break

          case 500:
            console.error('Internal server error')
            break

          default:
            console.error(`API Error ${status}:`, data)
        }
      } else if (error?.request) {
        // Network error
        console.error('Network error:', error?.message)
      } else {
        console.error('Request setup error:', error?.message)
      }
    } catch (handlingError) {
      console.error('Error in response interceptor:', handlingError)
    }

    return Promise.reject(error)
  }
)

// Generic API methods with proper typing for your API response format
export const api = {
  // GET request
  get: <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ServiceResponse<T>>> => {
    return apiClient.get<ServiceResponse<T>>(url, config)
  },

  // POST request
  post: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ServiceResponse<T>>> => {
    return apiClient.post<ServiceResponse<T>>(url, data, config)
  },

  // PUT request
  put: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ServiceResponse<T>>> => {
    return apiClient.put<ServiceResponse<T>>(url, data, config)
  },

  // PATCH request
  patch: <T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ServiceResponse<T>>> => {
    return apiClient.patch<ServiceResponse<T>>(url, data, config)
  },

  // DELETE request
  delete: <T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<ServiceResponse<T>>> => {
    return apiClient.delete<ServiceResponse<T>>(url, config)
  },
}

// Utility function to handle API responses consistently
export const handleApiResponse = <T>(
  response: AxiosResponse<ServiceResponse<T>>
): ServiceResponse<T> => {
  try {
    const responseData = response?.data

    // Validate response structure
    if (!responseData || typeof responseData !== 'object') {
      return {
        success: false,
        data: null,
        message: 'Invalid response format',
        status: response?.status || 0,
      }
    }

    return {
      success: responseData?.success ?? false,
      data: responseData?.success ? (responseData?.data ?? null) : null,
      message: responseData?.message || 'Request completed',
      status: response?.status,
    }
  } catch (error) {
    console.error('Error in handleApiResponse:', error)
    return {
      success: false,
      data: null,
      message: 'Error processing response',
      status: response?.status || 0,
    }
  }
}

// Utility function to handle API errors consistently
export const handleApiError = <T = any>(
  error: AxiosError
): ServiceResponse<T> => {
  try {
    if (error?.response) {
      const responseData = error.response.data as any

      return {
        success: false,
        data: null,
        status: error.response?.status,
        message:
          responseData?.message ||
          error?.message ||
          `HTTP ${error.response.status} Error`,
      }
    } else if (error?.request) {
      return {
        success: false,
        data: null,
        status: 0,
        message: 'Network error - please check your connection',
      }
    } else {
      return {
        success: false,
        data: null,
        status: 0,
        message: error?.message || 'Request setup error',
      }
    }
  } catch (handlingError) {
    console.error('Error in handleApiError:', handlingError)
    return {
      success: false,
      data: null,
      status: 0,
      message: 'Unexpected error occurred',
    }
  }
}

export default apiClient
